import { flow, Instance, types } from "mobx-state-tree";
import { CallReason } from "@closerplatform/closer-sdk";
import { mediaDevicesService } from "../../services/mediaDevicesService";
import { MediaService } from "../../services/mediaService";
import { logger } from "../../utils";
import { LocalMediaTrackModel, MediaTrackModel } from "./mediaTrack";
import { sharedServicesGetters } from "../shared/storeUtils";
import { TrackInfo, UserCallInfo } from "@fidget/common/services/AbstractCallService";

export enum CallState {
  CONNECTING = "CONNECTING",
  IN_PROGRESS = "IN_PROGRESS",
  ENDED = "ENDED"
}

export const CallModel = types
  .model("Call", {
    callId: types.maybe(types.string),
    remoteUserId: types.maybe(types.string),
    state: types.maybe(types.enumeration("CallState", Object.values(CallState))),
    startTimestamp: types.maybe(types.number),
    localMicrophone: types.optional(LocalMediaTrackModel, {}),
    localCamera: types.optional(LocalMediaTrackModel, {}),
    localScreen: types.optional(LocalMediaTrackModel, {}),
    remoteAudio: types.optional(MediaTrackModel, {}),
    remoteVideo: types.optional(MediaTrackModel, {})
  })
  .views(sharedServicesGetters)
  .views((self) => {
    return {
      get isInProgress(): boolean {
        return self.state === CallState.IN_PROGRESS;
      },
      get isConnecting(): boolean {
        return self.state === CallState.CONNECTING;
      },
      get isEnded(): boolean {
        return self.state === CallState.ENDED;
      },
      get isNoCall(): boolean {
        return !self.state || self.state === CallState.ENDED;
      }
    };
  })
  .actions((self) => {
    const callService = self.sharedCallService;

    return {
      stopMediaDevices() {
        logger.debug("CallStore: stopping media devices");
        [
          self.localMicrophone,
          self.localCamera,
          self.localScreen,
          self.remoteAudio,
          self.remoteVideo
        ].forEach((track) => track.stop());
      },
      stopScreenSharing() {
        self.localScreen.disable();
        self.localScreen.stop();
        // show back the camera stream when screen sharing is stopped
        callService.setVideoStream({ ...self.localCamera, content: "camera" });
      },
      reloadGrantedPermissions: flow(function* () {
        const perms = yield mediaDevicesService.reloadPermissions();
        if (perms.isWebsiteHasMicrophonePermissions) {
          self.localMicrophone.grantPermission();
        }
      }),
      clearCall() {
        self.callId = undefined;
        self.remoteUserId = undefined;
        self.startTimestamp = undefined;
        self.state = undefined;
      }
    };
  })
  .actions((self) => {
    const callService = self.sharedCallService;

    let endedOnLimit = false;
    callService.ended$.subscribe((call) => {
      const callJson = JSON.parse(JSON.stringify(call));
      if (callJson["reason"] === CallReason.TimeLimitExceeded) {
        endedOnLimit = true;
      }
    });

    return {
      handleConnecting(call: UserCallInfo) {
        logger.debug("CallStore: call connecting", call);
        self.callId = call.callId;
        self.remoteUserId = call.remoteUserId;
        self.state = CallState.CONNECTING;
        self.remoteAudio.enable();
      },
      handleEnded() {
        logger.debug("CallStore: call ended");
        if (endedOnLimit) {
          self.state = CallState.ENDED;
        } else {
          self.clearCall();
        }
        self.stopMediaDevices();
      },
      handleAnswered(call: UserCallInfo) {
        logger.debug("CallStore: call answered", call);
        self.state = CallState.IN_PROGRESS;
        self.startTimestamp = Date.now();
      }
    };
  })
  .actions((self) => {
    const callService = self.sharedCallService;
    const mediaService = MediaService.getInstance();

    return {
      handleRemoteTrackReceived(trackInfo: TrackInfo) {
        logger.debug("CallStore: received new track event", trackInfo);
        if (trackInfo.track && trackInfo.kind === "audio") {
          self.remoteAudio.setMediaStreamTrack(trackInfo.track);
        }
        if (trackInfo.track && trackInfo.kind === "video") {
          self.remoteVideo.setMediaStreamTrack(trackInfo.track);
        }
        // undefined means the status did not change
        if (trackInfo.enabled !== undefined) {
          const mediaTrack = trackInfo.kind === "audio" ? self.remoteAudio : self.remoteVideo;
          trackInfo.enabled ? mediaTrack.enable() : mediaTrack.disable();
        }
      },

      toggleCamera: flow(function* () {
        self.localCamera.toggle();
        if (self.localCamera.enabled && !self.localCamera.trackId) {
          // camera was enabled for the first time, request access from user
          const track = yield mediaService.getCameraTrack();
          if (track) {
            self.localCamera.setMediaStreamTrack(track);
          }
        }
        yield callService.setVideoStream({ ...self.localCamera, content: "camera" });
      }),

      toggleMicrophone: flow(function* () {
        self.localMicrophone.toggle();
        yield callService.setAudioStream(self.localMicrophone);
      }),

      send: flow(function* (type: string, message?: any) {
        if (!self.isNoCall && self.callId) {
          try {
            const data = JSON.stringify({ type, message });
            yield callService.send({ callId: self.callId }, data);
          } catch (error) {
            logger.error({ message: "Call send error", error });
          }
        }
      }),

      toggleScreen: flow(function* () {
        self.localScreen.toggle();
        if (self.localScreen.enabled) {
          const track = yield mediaService.getScreenTrack();
          if (track) {
            // user has granted access to the screen
            self.localScreen.setMediaStreamTrack(track);
            track.addEventListener("ended", self.stopScreenSharing);
            yield callService.setVideoStream({ ...self.localScreen, content: "screen" });
          } else {
            // screen sharing request was canceled
            self.localScreen.enabled = false;
          }
        } else {
          self.stopScreenSharing();
        }
      })
    };
  })
  .actions((self) => {
    const callService = self.sharedCallService;

    return {
      afterCreate() {
        callService.ended$.subscribe(() => self.handleEnded());
        callService.trackReceived$.subscribe((trackInfo) =>
          self.handleRemoteTrackReceived(trackInfo)
        );
      }
    };
  });

export type ICallModel = Instance<typeof CallModel>;
